<template>
  <div
    ref="column"
    :class="[
      $style.column,
      'board_column',
      column.sharedView ? $style.shared : '',
      taskDragging && $style.task_drag_active,
      isCollapsed && $style.collapsed,
      taskSettingsInfo && $style.task_settings_opened,
      isCollapsed && 'column_drag_handle'
    ]"
    @click="columnClickHandler"
  >
    <div :class="[$style.header, !isCollapsed && 'column_drag_handle']">
      <div :class="$style.info">
        <div :class="$style.main">
          <vgp-avatar
            v-if="column.picture"
            :class="$style.avatar"
            :src="column.picture"
            size="small"
          />
          <svg-sprite
            v-if="column.id === 'empty' && groupingFieldInfo.key === 'resources' && !groupingFieldInfo.isCustom"
            type="regular"
            :size="24"
            name="assigned"
            color="#333333"
            :style="{ minWidth: '24px' }"
          />
          <div
            :class="[$style.label, column.headerColor && $style.colored]"
            :style="{
              backgroundColor: column.headerColor
            }"
          >
            <span
              v-if="!column.noLabel"
              :style="{
                color: column.headerLabelColor || ''
              }"
            >
              {{ column.value.toUpperCase() }}
            </span>
          </div>
        </div>
        <div :class="$style.counter">
          {{ allTasks.length }}
        </div>
      </div>
      <div
        v-if="!column.sharedView"
        :class="$style.buttons"
      >
        <iconButton
          v-if="showCollapse"
          v-tippy="{ content: isCollapsed ? locale('board_column_expand') : locale('board_column_collapse'), duration: [300, 0], delay: [200, 0], placement: 'top' }"
          :icon="{ name: isCollapsed ? 'expand-4' : 'collaps-2', type: 'regular', size: 20 }"
          @onClick="collapseToggle"
        />
        <iconButton
          v-show="showCreate"
          v-tippy="{ content: locale('board_task_create'), duration: [300, 0], delay: [200, 0], placement: 'top' }"
          :icon="{ name: 'plus-1', type: 'regular', size: 20 }"
          @onClick="newTask"
        />
        <iconButton
          v-if="showMore"
          :icon="{ name: 'more-1', type: 'regular', size: 20 }"
          @onClick="(e) => toggleContextMenu(column, 'column', 'main', e.target)"
        />
      </div>
    </div>
    <div
      ref="cardsScrollWrapper"
      :class="[
        $style.column_content,
        'column_content',
        'gantt_scroll',
        showColumnShadow ? $style.shadowScroll : null
      ]"
    >
      <draggable
        ref="cardsDndContainer"
        :group="{ name: 'cards', put: canDragTasks ? ['cards'] : false }"
        :class="[
          $style.cards,
          isCollapsed ? $style.collapsed_column : '',
          taskDragging ? $style.drag_accept : ''
        ]"
        v-model="draggableList"
        :item-key="task => `${column.id}:${task.id}`"
        :style="{
          paddingTop: `${paddingTop}px`,
          paddingBottom: `${paddingBottom}px`
        }"
        :data-column-id="column.id"
        v-bind="{
          animation: 200,
          disabled: column.sharedView || taskCardEditing || taskSettingsInfo,
          draggable: '.' + $style.card_wrapper,
          ghostClass: $style.drop_preview,
          forceFallback: true,
          fallbackClass: 'card_drag',
          fallbackOnBody: true,
          fallbackTolerance: 5,
          easing: 'cubic-bezier(1, 0, 0, 1)',
          dataIdAttr: 'data-id',
          emptyInsertThreshold: 0,
          scrollSensitivity: 200,
          scrollSpeed: 25
        }"
        :move="onTaskMove"
        @start="onTaskDragStart"
        @end="onTaskDrop"
      >
        <template #item="{element: task}">
          <div
            v-if="task.isDraftTask"
            :key="`${column.id}:${task.id}`"
            :class="$style.new_card_wrapper"
          >
            <card
              :column-id="column.id"
              :task="task"
              :project-resources="projectResources"
            />
          </div>
          <div
            v-else
            :key="`${column.id}:${task.id}`"
            :class="$style.card_wrapper"
            :data-id="task.id"
          >
            <shared-card
              v-if="column.sharedView"
              :task="tasksMap[task.id]"
            />
            <card
              v-else-if="tasksMap[task.id]"
              :column-id="column.id"
              :task="tasksMap[task.id]"
              :project-resources="projectResources"
              @taskChange="(e) => $emit('taskChange', e)"
            />
          </div>
        </template>
      </draggable>
      <div :class="$style.bottom_part" @mouseover="mouseOver">
        <div
          v-if="emptyTask"
          ref="newCardWrapper"
          :class="$style.new_card_wrapper"
          :key="`${column.id}:create`"
        >
          <card
            :column-id="column.id"
            :task="{ id: 'create' }"
            @createTask="createTask"
          />
        </div>
        <iconButton
          v-show="showCreate"
          :class="$style.add_task_button"
          :icon="{ name: 'plus-1', type: 'regular', size: 20 }"
          :label="locale('board_task_create')"
          size="middle"
          @onClick="newTask"
        />
      </div>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import { mapGetters } from 'vuex';

import Card from './Card.vue';
import SharedCard from './SharedCard.vue';
import iconButton from '$$vueCmp/globalButton/withIcon/iconButton.vue';
import globalToast from '$$vueCmp/toast';
import { debounce } from '../../../helpers/boardHelpers';
import rights from '../../../components/rights';
import { locale } from 'moment';

export default {
  name: 'Column',
  components: {
    draggable,
    Card,
    SharedCard,
    iconButton,
  },
  inject: [
    'getTasksMap',
    'toggleContextMenu',
    'hideFilterPopup',
    'hideProjectSettingsPopup',
    'closeContextMenus',
    'getAllColumnsRefs',
  ],
  props: {
    column: {
      type: Object,
      required: true,
      default: {},
    },
    isCollapsed: {
      type: Boolean,
      required: true,
      default: false,
    },
    order: {
      type: Array, // [<column index (position - 1)>, <columns count>]
      required: true,
      default: [1, 1],
    },
    acceptDragItem: {
      type: Boolean,
      default: true,
    },
    taskDragging: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    canDragTasks: {
      type: Boolean,
      default: false
    },
    tasksMap: {
      type: Object,
      default: {}
    },
    projectResources: {
      type: Object,
      default: {}
    },
    boardHeight: {
      type: Number,
      default: 1500
    },
    draftTasks: {
      type: Array,
      default: []
    }
  },
  emits: [
    'taskChange',
    'taskDragStart',
    'onTaskDrop',
    'onCollapse',
    'createTask',
  ],
  data() {
    return {
      locale: __,
      emptyTask: false,
      scrollTop: 0,
      showColumnShadow: false,
      dragStartScrollTop: null,
      dragInfo: null,
      firstVisibleIndex: null,
    };
  },
  computed: {
    draggableList() {
      const list = [...this.visibleTasks];

      this.draftTasks.forEach(draftTask => {
        if (draftTask.columnId === this.column.id) {
          list.push({ ...draftTask, isDraftTask: true });
        }
      });

      return list;
    },

    isCustomField() {
      return !!this.groupingFieldInfo.isCustom;
    },

    contextMenuItems() {
      return {
        columnOrder: new Array(this.order[1]).fill(1).map((item, index) => ({
          name: `${index + 1} ${__('board_column_position')}`,
          id: `board_column_position_${index + 1}`,
          value: index,
          gpTestId: `gp_autotest_${index + 1}_board_column_position`,
          selected: this.order[0] === index,
        })),
      };
    },
    allTasks() {
      return [...this.column.tasks];
    },
    visibleTasks() {
      if (this.isCollapsed) return [];
      
      const boardHeight = document.querySelector('.board_container')?.clientHeight || 1000;
      let topEdge = this.scrollTop;
      let bottomEdge = this.scrollTop + boardHeight;

      if (typeof this.dragStartScrollTop === 'number') {
        if (this.dragStartScrollTop < this.scrollTop) {
          topEdge = this.dragStartScrollTop;
        } else {
          bottomEdge = this.dragStartScrollTop + boardHeight;
        }
      }

      let firstVisibleIndex = -1;

      const visibleTasks = [...this.allTasks].filter((task, i) => {
        const filterCondition = task.offsetTop > topEdge - 600 && task.offsetTop < (bottomEdge + 600 + this.boardHeight); 

        if (filterCondition && firstVisibleIndex === -1) {
          firstVisibleIndex = i;
        }

        return filterCondition;
      });

      this.firstVisibleIndex = firstVisibleIndex;

      return visibleTasks;
    },
    paddingTop() {
      if (this.isCollapsed) return 0;

      return this.visibleTasks[0]?.offsetTop || 0;
    },
    paddingBottom() {
      if (this.isCollapsed) return 0;

      const lastVisible = this.visibleTasks.at(-1);

      return this.column.totalHeight - ((lastVisible?.offsetTop || 0) + (lastVisible?.taskHeight || 0));
    },
    onScroll() {
      return debounce((e) => {
        if (e.target.scrollTop > 50 && e.target.scrollTop < this.column.totalHeight - e.target.clientHeight - 50) {
          this.showColumnShadow = true;
        } else {
          this.showColumnShadow = false;
        }
        this.scrollTop = e.target.scrollTop;
      }, 5);
    },
    canCreateTasks() {
      return rights.project.hasRight(this.projectId, 'create_task');
    },
    showCollapse() {
      return !(this.emptyTask || (this.taskSettingsInfo && !this.isCollapsed));
    },
    showCreate() {
      return !this.column.sharedView && !this.isCollapsed && this.canCreateTasks && !(this.emptyTask || this.taskSettingsInfo);
    },
    showMore() {
      return !(this.isCollapsed || this.taskSettingsInfo);
    },
    ...mapGetters('board', ['allFields', 'taskCardEditing', 'taskSettingsInfo', 'groupingFieldInfo', 'projectId']),
  },
  watch: {
    taskDragging(newValue) {
      if (newValue && this.emptyTask) {
        this.emptyTask = false;
        this.scrollColumnTo({ bottom: true });
      }
    },
    draftTasks: {
      handler(newValue) {
        if (newValue.length && newValue.find(task => task.columnId === this.column.id)) {
          this.scrollColumnTo({ bottom: true });
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.$refs.cardsScrollWrapper.addEventListener('scroll', this.onScroll);
  },
  beforeUnmount() {
    this.$refs.cardsScrollWrapper.removeEventListener('scroll', this.onScroll);
  },
  methods: {
    onTaskMove() {
    },
    onTaskDragStart(e) {
      this.hideFilterPopup();
      this.hideProjectSettingsPopup();
      this.closeContextMenus();

      if (!this.canDragTasks) {
        globalToast.info(__('board_permission_restriction'), { timeout: 5000 });
      }

      this.dragStartScrollTop = this.$refs.cardsScrollWrapper.scrollTop;
      this.dragInfo = {
        dragItem: e.item._underlying_vm_,
        columnFrom: this.column.id,
        indexFrom: this.firstVisibleIndex + e.oldIndex,
      };
      this.$emit('taskDragStart', {
        task: e.item._underlying_vm_,
        columnFrom: this.column.id,
      });
    },
    onTaskDrop(e) {
      const { dragItem, columnFrom, indexFrom } = this.dragInfo;
      let columnTo;
      let indexTo;

      if (e.from === e.to) {
        columnTo = this.column.id;
        indexTo = this.firstVisibleIndex + e.newIndex;
      } else {
        const toColumnVue = this.getAllColumnsRefs().find(columnRef => columnRef.$el.contains(e.to));

        columnTo = toColumnVue.column.id;
        indexTo = toColumnVue.firstVisibleIndex + e.newIndex;
      }

      this.$emit('onTaskDrop', {
        dragItem,
        columnFrom,
        indexFrom,
        columnTo,
        indexTo,
      });

      setTimeout(() => {
        this.dragStartScrollTop = null;
        this.dragInfo = null;
      }, 0);
    },
    mouseOver(e) {
      if (!this.taskDragging) return;

      this.$refs.cardsDndContainer._sortable._onDragOver({
        clientX: e.clientX,
        clientY: e.clientY,
        target: this.$refs.cardsDndContainer.$el,
        rootEl: this.$refs.cardsDndContainer.$el
      });
    },
    collapseToggle() {
      this.$emit('onCollapse', this.column.id);
    },
    columnClickHandler(e) {
      if (!this.isCollapsed || this.dragInfo || e.target.closest(`.${this.$style.buttons}`) || this.taskSettingsInfo) return;

      this.collapseToggle();
    },
    newTask() {
      this.emptyTask = true;
      this.$nextTick(() => {
        this.scrollColumnTo({ bottom: true,  });
        this.$refs.newCardWrapper.querySelector('textarea').focus();
      });
    },
    createTask(name, isOutsideClick) {
      if (!name && isOutsideClick) {
        this.emptyTask = false;
        this.scrollColumnTo({ bottom: true });
        return;
      }

      this.$emit('createTask', name, this.column.id, this);
    },
    scrollColumnTo({ bottom, top, y }) {
      const pos = bottom ? this.$refs.cardsScrollWrapper.scrollHeight : top ? 0 : y;
      this.$refs.cardsScrollWrapper.scrollTo(0, pos); 
    }
  },
};
</script>

<style module src="../less/column.less"></style>
